import React, { useMemo } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	DialogContent,
	Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { toRelationshipContactInitialValues } from '@/components/forms/CreateRelationshipContactForm/fields';
import RelationshipContactFormFields from '@/components/forms/CreateRelationshipContactForm/Fields/RelationshipContactFormFields';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import {
	useGetRelationshipContactQuery,
	useUpdateRelationshipContactMutation,
} from '@/features/relationshipContacts/relationshipContactsApi';
import { difference } from '@/utils/constants';
import validationSchema from '@/components/forms/CreateRelationshipContactForm/validationSchema';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import RelationshipContactHeader from './RelationshipContactHeader';
import AssociatedCompanies from './AssociatedCompanies';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const EditRelationshipContact = ({ contactId, onClose }) => {
	const dispatch = useDispatch();
	const { relationshipCompany } = useObjectContext();
	const { getFeatureFlags } = useFeatureFlagContext();
	const isRelationshipContactsEnabled = Boolean(getFeatureFlags('relationshipContacts')?.active);
	const { data: contact, isLoading } = useGetRelationshipContactQuery(
		{ relationshipContactId: contactId },
		{ skip: !contactId },
	);
	const initialValues = useMemo(() => {
		const relationshipCompanyId = relationshipCompany?.id;
		const association = (contact?.associations ?? []).find(
			(row) => relationshipCompanyId && row.relationshipCompanyId === relationshipCompanyId,
		);

		return toRelationshipContactInitialValues({
			...contact,
			relationshipCompanyId,
			relationshipCompanyName: relationshipCompany?.name,
			isPrimary: Boolean(association?.isPrimary),
		});
	}, [contact, relationshipCompany]);
	const [updateRelationshipContact, updateState] = useUpdateRelationshipContactMutation();

	if (isLoading) {
		return (
			<>
				<FolioDrawerHeader title='Relationship Details' />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '200px',
					}}
				>
					<CircularProgress />
				</Box>
			</>
		);
	}

	if (!contact) {
		return (
			<>
				<FolioDrawerHeader title='Relationship Details' />
				<Typography variant='body1' color='error' sx={{ p: 2 }}>
					Error retrieving contact.
				</Typography>
			</>
		);
	}

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			enableReinitialize
			validateOnBlur
			onSubmit={async (values, { setSubmitting }) => {
				setSubmitting(true);
				try {
					const valuesToSave = Object.fromEntries(
						Object.entries(values).map(([key, value]) => [
							key,
							typeof value === 'string' ? value.trim() : value,
						]),
					);
					const diffFields = difference(initialValues, valuesToSave);
					const {
						relationshipCompanyId,
						relationshipCompanyName: _relationshipCompanyName,
						isPrimary,
						...contactProperties
					} = diffFields;

					if (
						Object.keys(contactProperties).length === 0 &&
						relationshipCompanyId == null &&
						isPrimary == null
					) {
						dispatch(
							showToast({
								message: 'No changes to update.',
								autohide: true,
							}),
						);
						return;
					}

					await updateRelationshipContact({
						relationshipContactId: contactId,
						relationshipCompanyId: values.relationshipCompanyId,
						isPrimary,
						properties: contactProperties,
					}).unwrap();

					dispatch(
						showToast({
							message: 'Updated contact!',
							success: true,
							autohide: true,
							duration: 6000,
						}),
					);

					if (diffFields.relationshipStatus === 'archived') {
						onClose();
					}
				} catch (error) {
					dispatch(
						showToast({
							message:
								error?.data?.message ??
								error?.message ??
								'Unable to update contact.',
							error: true,
						}),
					);
				} finally {
					setSubmitting(false);
				}
			}}
		>
			{({ isSubmitting, handleSubmit, validateForm }) => {
				const disabled = isSubmitting || updateState.isLoading;

				return (
					<Form
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						<FolioDrawerHeader title='Relationship Details' />
						<Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
							<FormikErrorScroll isSubmitting={isSubmitting} />
							<RelationshipContactHeader />
							{isRelationshipContactsEnabled && (
								<Box sx={{ mt: 2 }}>
									<AssociatedCompanies associations={contact.associations} />
								</Box>
							)}
							<DialogContent sx={{ px: 0 }}>
								<RelationshipContactFormFields
									disabled={disabled}
									relationshipContactId={contactId}
								/>
							</DialogContent>
						</Box>
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								color='primary'
								variant='contained'
								disabled={disabled}
								onClick={async () => {
									handleSubmit();
									setTimeout(() => {
										validateForm();
									}, 100);
								}}
							>
								{disabled ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Update'
								)}
							</Button>
						</FolioDrawerFooter>
					</Form>
				);
			}}
		</Formik>
	);
};

export default EditRelationshipContact;
